import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import { Skeleton, SkeletonBlock } from '../components/Layout';
import {
  getAccounts, createAccount, updateAccount, suspendAccount, activateAccount,
  getPlans, createPlan, updatePlan, deletePlan,
} from '../api/client';
import client from '../api/client';
import { C, Btn, Toast, useNotify } from '../utils/styles';

// ── Modal ─────────────────────────────────────────────────────
const Modal = ({ title, icon, onClose, children, width=520 }) => (
  <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(15,15,15,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000,padding:20}}>
    <div onClick={e=>e.stopPropagation()} style={{width,maxWidth:'100%',maxHeight:'88vh',overflowY:'auto',background:'#fff',borderRadius:16,padding:24,boxShadow:'0 25px 60px rgba(0,0,0,0.3)'}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:18}}>
        <div style={{fontWeight:600,fontSize:15,color:C.dark,display:'flex',alignItems:'center',gap:8}}>
          <i className={`ti ${icon}`} style={{color:C.primary,fontSize:18}}/>{title}
        </div>
        <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:C.muted,fontSize:18,padding:4}}><i className="ti ti-x"/></button>
      </div>
      {children}
    </div>
  </div>
);

const inp = {width:'100%',padding:'8px 12px',borderRadius:8,border:`1px solid ${C.border}`,fontSize:13,outline:'none',boxSizing:'border-box'};
const labelStyle = {fontSize:12,fontWeight:500,color:C.text,marginBottom:4,display:'block'};
const TYPE_COLORS = {platform:'#8b5cf6',agency:'#0099FF',sub:'#22c55e'};

const FeatureToggle = ({label,checked,onChange}) => (
  <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'9px 0',borderBottom:`1px solid ${C.border}`}}>
    <span style={{fontSize:13,color:C.text}}>{label}</span>
    <button type="button" onClick={()=>onChange(!checked)}
      style={{width:40,height:22,borderRadius:11,border:'none',cursor:'pointer',background:checked?C.primary:'#d1d5db',position:'relative',transition:'background 0.2s',flexShrink:0}}>
      <span style={{position:'absolute',top:3,left:checked?20:3,width:16,height:16,borderRadius:'50%',background:'#fff',transition:'left 0.2s',boxShadow:'0 1px 3px rgba(0,0,0,0.2)'}}/>
    </button>
  </div>
);

const AccountSkeleton = () => (
  <div style={{padding:'16px 20px',borderBottom:`1px solid ${C.border}`,display:'flex',alignItems:'center',gap:14}}>
    <Skeleton circle size={42}/>
    <div style={{flex:1}}><Skeleton height={13} width="35%" style={{marginBottom:6}}/><Skeleton height={11} width="55%"/></div>
    <Skeleton height={28} width={70} radius={6}/>
    <Skeleton height={28} width={60} radius={6}/>
  </div>
);

const PlanSkeleton = () => (
  <div style={{background:'#fff',borderRadius:12,border:`1px solid ${C.border}`,padding:20}}>
    <Skeleton height={14} width="50%" style={{marginBottom:8}}/>
    <Skeleton height={24} width="35%" style={{marginBottom:12}}/>
    <SkeletonBlock rows={4} gap={8}/>
  </div>
);

export default function Platform({ tab }) {
  const { isOwner } = useAuth();
  const { brand }   = useBrand();
  const [notification, notify] = useNotify();
  const [copiedPlanId, setCopiedPlanId] = useState('');

  // ── Accounts state ────────────────────────────────────────
  const [accounts,    setAccounts]    = useState([]);
  const [accsLoading, setAccsLoading] = useState(true);
  const [showNewAcct, setShowNewAcct] = useState(false);
  const [newAcct,     setNewAcct]     = useState({name:'',type:'sub',plan_id:'',owner_email:'',owner_name:''});
  const [acctSaving,  setAcctSaving]  = useState(false);
  const [editAcct,    setEditAcct]    = useState(null);
  const [deleteAcct,  setDeleteAcct]  = useState(null);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [deleting,    setDeleting]    = useState(false);

  // ── Plans state ───────────────────────────────────────────
  const [plans,        setPlans]        = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [showNewPlan,  setShowNewPlan]  = useState(false);
  const [editPlan,     setEditPlan]     = useState(null);
  const [planSaving,   setPlanSaving]   = useState(false);

  const defaultPlanForm = {
    name:'',account_type:'sub',
    price_monthly_inr:0,price_yearly_inr:0,sub_account_limit:0,
    features:{white_label:false,ai:false,custom_domain:false,contacts_limit:1000,users_limit:5},
    is_active:true,sort_order:99,trial_days:14,
  };
  const [planForm, setPlanForm] = useState(defaultPlanForm);

  useEffect(() => { fetchAccounts(); fetchPlans(); }, []); // eslint-disable-line

  const fetchAccounts = async () => {
    setAccsLoading(true);
    try {
      const { data } = await getAccounts();
      setAccounts(data||[]);
    } catch { notify('Failed to load accounts','error'); }
    finally { setAccsLoading(false); }
  };

  const fetchPlans = async () => {
    setPlansLoading(true);
    try { const { data } = await getPlans(); setPlans(data||[]); }
    catch { notify('Failed to load plans','error'); }
    finally { setPlansLoading(false); }
  };

  // Filter out the platform account from the list — it's always hidden
  const visibleAccounts = accounts.filter(a => a.type !== 'platform');

  // ── Copy signup link ──────────────────────────────────────
  const copySignupLink = (planId, planName) => {
    const link = `${window.location.origin}/register?plan=${planId}`;
    navigator.clipboard.writeText(link).then(() => {
      setCopiedPlanId(planId);
      notify(`✅ Signup link for "${planName}" copied!`);
      setTimeout(() => setCopiedPlanId(''), 3000);
    }).catch(() => {
      const el = document.createElement('textarea');
      el.value = link;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiedPlanId(planId);
      notify(`✅ Signup link copied!`);
      setTimeout(() => setCopiedPlanId(''), 3000);
    });
  };

  // ── Account handlers ──────────────────────────────────────
  const handleCreateAccount = async () => {
    if (!newAcct.name) return notify('Account name is required','error');
    setAcctSaving(true);
    try {
      await createAccount(newAcct);
      notify('Account created!');
      setShowNewAcct(false);
      setNewAcct({name:'',type:'sub',plan_id:'',owner_email:'',owner_name:''});
      fetchAccounts();
    } catch (err) { notify(err.response?.data?.error||'Failed','error'); }
    finally { setAcctSaving(false); }
  };

  const handleUpdateAccount = async () => {
    if (!editAcct) return;
    try {
      await updateAccount(editAcct.id,{name:editAcct.name,plan_id:editAcct.plan_id,status:editAcct.status});
      notify('Account updated!');
      setEditAcct(null);
      fetchAccounts();
    } catch (err) { notify(err.response?.data?.error||'Failed','error'); }
  };

  const handleSuspend  = async (id) => {
    if (!window.confirm('Suspend this account?')) return;
    try { await suspendAccount(id); notify('Suspended'); fetchAccounts(); }
    catch (err) { notify(err.response?.data?.error||'Failed','error'); }
  };

  const handleActivate = async (id) => {
    try { await activateAccount(id); notify('Activated'); fetchAccounts(); }
    catch (err) { notify(err.response?.data?.error||'Failed','error'); }
  };

  const handleSwitchInto = (accountId) => {
    localStorage.setItem('dx_active_account', accountId);
    window.location.href = '/';
  };

  const handleDeleteAccount = async () => {
    if (!deleteAcct || deleteConfirmName !== deleteAcct.name)
      return notify('Account name does not match','error');
    setDeleting(true);
    try {
      await client.delete(`/api/accounts/${deleteAcct.id}`,{data:{confirmName:deleteConfirmName}});
      notify(`"${deleteAcct.name}" deleted permanently`);
      setDeleteAcct(null); setDeleteConfirmName('');
      fetchAccounts();
    } catch (err) {
      const code = err.response?.data?.code;
      const msg  = err.response?.data?.error || 'Failed to delete';
      if (code === 'PROTECTED_ACCOUNT') {
        setDeleteAcct(null); setDeleteConfirmName('');
      }
      notify(msg,'error');
    }
    finally { setDeleting(false); }
  };

  // ── Plan handlers ─────────────────────────────────────────
  const openNewPlan  = () => { setPlanForm(defaultPlanForm); setEditPlan(null); setShowNewPlan(true); };
  const openEditPlan = (plan) => {
    setPlanForm({
      name:plan.name,account_type:plan.account_type,
      price_monthly_inr:plan.price_monthly_inr,price_yearly_inr:plan.price_yearly_inr,
      sub_account_limit:plan.sub_account_limit,features:plan.features||defaultPlanForm.features,
      is_active:plan.is_active,sort_order:plan.sort_order,trial_days:plan.trial_days||14,
    });
    setEditPlan(plan); setShowNewPlan(true);
  };

  const handleSavePlan = async () => {
    if (!planForm.name) return notify('Plan name is required','error');
    setPlanSaving(true);
    try {
      if (editPlan) { await updatePlan(editPlan.id,planForm); notify('Plan updated!'); }
      else          { await createPlan(planForm); notify('Plan created!'); }
      setShowNewPlan(false); setEditPlan(null); fetchPlans();
    } catch (err) { notify(err.response?.data?.error||'Failed','error'); }
    finally { setPlanSaving(false); }
  };

  const handleDeletePlan = async (plan) => {
    if (!window.confirm(`Delete plan "${plan.name}"?`)) return;
    try { await deletePlan(plan.id); notify('Plan deleted'); fetchPlans(); }
    catch (err) { notify(err.response?.data?.error||'Cannot delete plan — accounts may be using it','error'); }
  };

  const pf = (n) => `₹${Number(n||0).toLocaleString('en-IN')}`;
  const currentTab = tab || 'accounts';

  return (
    <div>
      <Toast {...notification}/>

      <div style={{marginBottom:24}}>
        <h1 style={{fontSize:20,fontWeight:700,color:C.dark,marginBottom:2}}>
          {currentTab === 'accounts' ? 'Accounts' : 'Plans'}
        </h1>
        <p style={{fontSize:13,color:C.muted}}>
          {currentTab === 'accounts'
            ? `Manage all accounts for ${brand.app_name||'Digital Xtension'} — ${visibleAccounts.length} account${visibleAccounts.length!==1?'s':''}`
            : `Manage pricing plans — ${plans.length} plan${plans.length!==1?'s':''}`}
        </p>
      </div>

      {/* ── ACCOUNTS VIEW ── */}
      {currentTab === 'accounts' && (
        <div>
          <div style={{display:'flex',justifyContent:'flex-end',marginBottom:16}}>
            <Btn onClick={()=>setShowNewAcct(true)}><i className="ti ti-plus"/> New Account</Btn>
          </div>

          <div style={{background:'#fff',borderRadius:12,border:`1px solid ${C.border}`,overflow:'hidden'}}>
            {accsLoading ? (
              Array(4).fill(null).map((_,i) => <AccountSkeleton key={i}/>)
            ) : visibleAccounts.length === 0 ? (
              <div style={{padding:48,textAlign:'center',color:C.faint}}>
                <i className="ti ti-building" style={{fontSize:36,display:'block',marginBottom:12,color:'#d1d5db'}}/>
                <div style={{fontWeight:600,fontSize:14,color:C.dark,marginBottom:8}}>No accounts yet</div>
                <Btn onClick={()=>setShowNewAcct(true)}><i className="ti ti-plus"/> Create First Account</Btn>
              </div>
            ) : visibleAccounts.map((acc, i) => (
              <div key={acc.id} style={{
                padding:'14px 20px',
                borderBottom: i < visibleAccounts.length-1 ? `1px solid ${C.border}` : 'none',
                display:'flex',alignItems:'center',gap:14,
              }}>
                {/* Avatar */}
                <div style={{width:42,height:42,borderRadius:10,background:TYPE_COLORS[acc.type]||C.primary,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,overflow:'hidden'}}>
                  {acc.logo_url
                    ? <img src={acc.logo_url} alt={acc.name} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                    : <span style={{color:'#fff',fontSize:16,fontWeight:700}}>{acc.name?.charAt(0).toUpperCase()}</span>}
                </div>

                {/* Account info */}
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:3,flexWrap:'wrap'}}>
                    <span style={{fontWeight:600,fontSize:13,color:C.dark}}>{acc.name}</span>
                    {acc.is_protected && (
                      <span style={{fontSize:10,fontWeight:700,padding:'1px 7px',borderRadius:20,background:'#faf5ff',color:'#7c3aed',display:'flex',alignItems:'center',gap:3}}>
                        <i className="ti ti-shield-lock" style={{fontSize:10}}/> Protected
                      </span>
                    )}
                    <span style={{fontSize:10,fontWeight:600,padding:'1px 7px',borderRadius:20,background:`${TYPE_COLORS[acc.type]||C.primary}18`,color:TYPE_COLORS[acc.type]||C.primary}}>
                      {acc.type==='sub'?'Sub Account':acc.type==='agency'?'Agency':acc.type}
                    </span>
                    <span style={{fontSize:10,padding:'1px 7px',borderRadius:20,
                      background:acc.status==='active'?'#f0fdf4':acc.status==='suspended'?'#fef2f2':'#f9fafb',
                      color:acc.status==='active'?C.success:acc.status==='suspended'?C.danger:C.muted}}>
                      {acc.status}
                    </span>
                  </div>
                  <div style={{fontSize:11,color:C.muted,display:'flex',gap:10,flexWrap:'wrap'}}>
                    {acc.plan?.name && <span>Plan: <b>{acc.plan.name}</b></span>}
                    {acc.owner_name  && <span>Owner: <b>{acc.owner_name}</b></span>}
                    {acc.owner_email && <span style={{color:C.faint}}>{acc.owner_email}</span>}
                    <span>Created {new Date(acc.created_at).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'})}</span>
                  </div>
                </div>

                {/* Actions */}
                <div style={{display:'flex',gap:6,flexShrink:0,alignItems:'center'}}>
                  <button onClick={()=>handleSwitchInto(acc.id)} title="Enter account"
                    style={{padding:'5px 12px',borderRadius:6,border:`1px solid ${C.primary}`,background:'var(--color-primary-light)',color:C.primary,cursor:'pointer',fontSize:12,display:'flex',alignItems:'center',gap:4,fontWeight:500}}>
                    <i className="ti ti-login" style={{fontSize:13}}/> Enter
                  </button>
                  <button onClick={()=>setEditAcct({...acc})} title="Edit"
                    style={{padding:'5px 8px',borderRadius:6,border:'none',background:'var(--color-primary-light)',color:C.primary,cursor:'pointer',fontSize:13}}>
                    <i className="ti ti-edit"/>
                  </button>
                  {!acc.is_protected && (
                    acc.status==='suspended'
                      ? <button onClick={()=>handleActivate(acc.id)} title="Activate"
                          style={{padding:'5px 8px',borderRadius:6,border:'none',background:'#f0fdf4',color:C.success,cursor:'pointer',fontSize:13}}>
                          <i className="ti ti-player-play"/>
                        </button>
                      : <button onClick={()=>handleSuspend(acc.id)} title="Suspend"
                          style={{padding:'5px 8px',borderRadius:6,border:'none',background:'#fef3c7',color:'#d97706',cursor:'pointer',fontSize:13}}>
                          <i className="ti ti-player-pause"/>
                        </button>
                  )}
                  {acc.is_protected ? (
                    <div title="Protected account — cannot be deleted"
                      style={{padding:'5px 8px',borderRadius:6,background:'#faf5ff',color:'#7c3aed',fontSize:13,display:'flex',alignItems:'center'}}>
                      <i className="ti ti-shield-lock"/>
                    </div>
                  ) : (
                    <button onClick={()=>{setDeleteAcct(acc);setDeleteConfirmName('');}} title="Delete"
                      style={{padding:'5px 8px',borderRadius:6,border:'none',background:'#fef2f2',color:C.danger,cursor:'pointer',fontSize:13}}>
                      <i className="ti ti-trash"/>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── PLANS VIEW ── */}
      {currentTab === 'plans' && (
        <div>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:16}}>
            <div style={{fontSize:13,color:C.muted}}>
              <i className="ti ti-link" style={{marginRight:5,color:C.primary}}/>
              Click <strong>Copy Link</strong> on any plan to get a public signup URL to share with clients.
            </div>
            <Btn onClick={openNewPlan}><i className="ti ti-plus"/> New Plan</Btn>
          </div>

          {plansLoading ? (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:16}}>
              {Array(3).fill(null).map((_,i) => <PlanSkeleton key={i}/>)}
            </div>
          ) : plans.length === 0 ? (
            <div style={{background:'#fff',borderRadius:12,border:`2px dashed ${C.border}`,padding:48,textAlign:'center'}}>
              <i className="ti ti-credit-card" style={{fontSize:36,display:'block',marginBottom:12,color:'#d1d5db'}}/>
              <div style={{fontWeight:600,fontSize:14,color:C.dark,marginBottom:8}}>No plans yet</div>
              <Btn onClick={openNewPlan}><i className="ti ti-plus"/> Create First Plan</Btn>
            </div>
          ) : (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:16}}>
              {plans.map(plan => (
                <div key={plan.id} style={{background:'#fff',borderRadius:12,border:`1px solid ${plan.is_active?C.border:'#f3f4f6'}`,padding:20,opacity:plan.is_active?1:0.6}}>
                  <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:12}}>
                    <div>
                      <div style={{fontWeight:700,fontSize:15,color:C.dark,marginBottom:4}}>{plan.name}</div>
                      <span style={{fontSize:10,fontWeight:600,padding:'2px 8px',borderRadius:20,background:'var(--color-primary-light)',color:C.primary}}>
                        {plan.account_type==='sub'?'Sub Account':'Agency Account'}
                      </span>
                    </div>
                    <div style={{display:'flex',gap:4}}>
                      <button onClick={()=>openEditPlan(plan)} style={{padding:'4px 8px',borderRadius:6,border:'none',background:'var(--color-primary-light)',color:C.primary,cursor:'pointer',fontSize:12}}>
                        <i className="ti ti-edit"/>
                      </button>
                      {isOwner && (
                        <button onClick={()=>handleDeletePlan(plan)} style={{padding:'4px 8px',borderRadius:6,border:'none',background:'#fef2f2',color:C.danger,cursor:'pointer',fontSize:12}}>
                          <i className="ti ti-trash"/>
                        </button>
                      )}
                    </div>
                  </div>

                  <div style={{marginBottom:12}}>
                    <div style={{fontSize:24,fontWeight:700,color:C.dark}}>{pf(plan.price_monthly_inr)}<span style={{fontSize:12,color:C.muted,fontWeight:400}}>/mo</span></div>
                    {plan.price_yearly_inr>0&&<div style={{fontSize:11,color:C.muted}}>{pf(plan.price_yearly_inr)}/yr</div>}
                    {(plan.trial_days||0)>0&&<div style={{fontSize:11,color:'#16a34a',marginTop:3}}><i className="ti ti-gift" style={{marginRight:4}}/>{plan.trial_days}-day free trial</div>}
                  </div>

                  <div style={{display:'flex',flexDirection:'column',gap:5,fontSize:12,marginBottom:14}}>
                    <div style={{display:'flex',justifyContent:'space-between',color:C.text}}>
                      <span>Sub-accounts</span><span style={{fontWeight:600}}>{plan.sub_account_limit===-1?'Unlimited':plan.sub_account_limit}</span>
                    </div>
                    <div style={{display:'flex',justifyContent:'space-between',color:C.text}}>
                      <span>Contacts</span><span style={{fontWeight:600}}>{plan.features?.contacts_limit===-1?'Unlimited':Number(plan.features?.contacts_limit||0).toLocaleString()}</span>
                    </div>
                    <div style={{display:'flex',justifyContent:'space-between',color:C.text}}>
                      <span>Users</span><span style={{fontWeight:600}}>{plan.features?.users_limit===-1?'Unlimited':plan.features?.users_limit}</span>
                    </div>
                    <div style={{display:'flex',gap:5,flexWrap:'wrap',marginTop:4}}>
                      {plan.features?.ai          &&<span style={{fontSize:10,padding:'2px 7px',borderRadius:20,background:'#f0fdf4',color:C.success}}>AI</span>}
                      {plan.features?.white_label &&<span style={{fontSize:10,padding:'2px 7px',borderRadius:20,background:'#f0f9ff',color:C.primary}}>White-label</span>}
                      {plan.features?.custom_domain&&<span style={{fontSize:10,padding:'2px 7px',borderRadius:20,background:'#faf5ff',color:'#7c3aed'}}>Custom Domain</span>}
                    </div>
                  </div>

                  <button onClick={()=>copySignupLink(plan.id,plan.name)}
                    style={{width:'100%',padding:'8px 12px',borderRadius:8,border:`1px solid ${copiedPlanId===plan.id?C.success:C.border}`,background:copiedPlanId===plan.id?'#f0fdf4':'#f9fafb',color:copiedPlanId===plan.id?C.success:C.text,fontSize:12,fontWeight:500,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:6,transition:'all 0.2s'}}>
                    <i className={`ti ${copiedPlanId===plan.id?'ti-check':'ti-link'}`} style={{fontSize:13}}/>
                    {copiedPlanId===plan.id?'Link Copied!':'Copy Signup Link'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── New Account Modal ── */}
      {showNewAcct && (
        <Modal title="Create New Account" icon="ti-building" onClose={()=>setShowNewAcct(false)}>
          <div style={{display:'grid',gap:12,marginBottom:16}}>
            <div><label style={labelStyle}>Account Name *</label><input style={inp} placeholder="e.g. Acme Agency" value={newAcct.name} onChange={e=>setNewAcct({...newAcct,name:e.target.value})}/></div>
            <div>
              <label style={labelStyle}>Account Type</label>
              <select style={inp} value={newAcct.type} onChange={e=>setNewAcct({...newAcct,type:e.target.value})}>
                <option value="sub">Sub Account</option>
                <option value="agency">Agency Account</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>Plan</label>
              <select style={inp} value={newAcct.plan_id} onChange={e=>setNewAcct({...newAcct,plan_id:e.target.value})}>
                <option value="">No plan (manual)</option>
                {plans.filter(p=>p.is_active).map(p=><option key={p.id} value={p.id}>{p.name} — {pf(p.price_monthly_inr)}/mo</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Owner Email <span style={{color:C.faint,fontWeight:400}}>(invite sent on creation)</span></label>
              <input style={inp} type="email" placeholder="owner@client.com" value={newAcct.owner_email} onChange={e=>setNewAcct({...newAcct,owner_email:e.target.value})}/>
            </div>
            <div>
              <label style={labelStyle}>Owner Name <span style={{color:C.faint,fontWeight:400}}>(optional)</span></label>
              <input style={inp} placeholder="John Doe" value={newAcct.owner_name} onChange={e=>setNewAcct({...newAcct,owner_name:e.target.value})}/>
            </div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <Btn onClick={handleCreateAccount} disabled={acctSaving}><i className="ti ti-plus"/> {acctSaving?'Creating...':'Create Account'}</Btn>
            <Btn variant="outline" onClick={()=>setShowNewAcct(false)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* ── Edit Account Modal ── */}
      {editAcct && (
        <Modal title={`Edit — ${editAcct.name}`} icon="ti-edit" onClose={()=>setEditAcct(null)}>
          <div style={{display:'grid',gap:12,marginBottom:16}}>
            <div><label style={labelStyle}>Account Name</label><input style={inp} value={editAcct.name} onChange={e=>setEditAcct({...editAcct,name:e.target.value})}/></div>
            <div>
              <label style={labelStyle}>Plan</label>
              <select style={inp} value={editAcct.plan_id||''} onChange={e=>setEditAcct({...editAcct,plan_id:e.target.value})}>
                <option value="">No plan</option>
                {plans.filter(p=>p.is_active).map(p=><option key={p.id} value={p.id}>{p.name} — {pf(p.price_monthly_inr)}/mo</option>)}
              </select>
            </div>
            <div>
              <label style={labelStyle}>Status</label>
              <select style={inp} value={editAcct.status} onChange={e=>setEditAcct({...editAcct,status:e.target.value})}>
                <option value="active">Active</option>
                <option value="suspended">Suspended</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <Btn onClick={handleUpdateAccount}><i className="ti ti-device-floppy"/> Save</Btn>
            <Btn variant="outline" onClick={()=>setEditAcct(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* ── Delete Confirmation Modal ── */}
      {deleteAcct && (
        <Modal title="Delete Account" icon="ti-alert-triangle" onClose={()=>{setDeleteAcct(null);setDeleteConfirmName('');}}>
          <div style={{background:'#fef2f2',border:'1px solid #fecaca',borderRadius:10,padding:14,marginBottom:16}}>
            <div style={{fontWeight:600,fontSize:13,color:'#991b1b',marginBottom:6}}>⚠️ This action is permanent and cannot be undone.</div>
            <div style={{fontSize:12,color:'#7f1d1d',lineHeight:1.6}}>
              Deleting <strong>{deleteAcct.name}</strong> will permanently remove all contacts, campaigns, bookings, users and settings. This data cannot be recovered.
            </div>
          </div>
          <div style={{marginBottom:16}}>
            <label style={{...labelStyle,marginBottom:8}}>
              Type the account name to confirm: <strong style={{color:C.danger}}>{deleteAcct.name}</strong>
            </label>
            <input style={{...inp,borderColor:deleteConfirmName&&deleteConfirmName!==deleteAcct.name?C.danger:C.border}}
              placeholder="Type account name to confirm..."
              value={deleteConfirmName}
              onChange={e=>setDeleteConfirmName(e.target.value)}
              onKeyDown={e=>e.key==='Enter'&&handleDeleteAccount()}
            />
            {deleteConfirmName&&deleteConfirmName!==deleteAcct.name&&(
              <div style={{fontSize:11,color:C.danger,marginTop:4}}>Name does not match</div>
            )}
          </div>
          <div style={{display:'flex',gap:8}}>
            <button onClick={handleDeleteAccount} disabled={deleting||deleteConfirmName!==deleteAcct.name}
              style={{padding:'9px 16px',borderRadius:8,border:'none',background:deleteConfirmName===deleteAcct.name?C.danger:'#f3f4f6',color:deleteConfirmName===deleteAcct.name?'#fff':'#9ca3af',fontSize:13,fontWeight:600,cursor:deleting||deleteConfirmName!==deleteAcct.name?'not-allowed':'pointer',display:'flex',alignItems:'center',gap:6}}>
              <i className={`ti ${deleting?'ti-loader ti-spin':'ti-trash'}`}/>{deleting?'Deleting...':'Delete Permanently'}
            </button>
            <Btn variant="outline" onClick={()=>{setDeleteAcct(null);setDeleteConfirmName('');}}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* ── Plan Modal ── */}
      {showNewPlan && (
        <Modal title={editPlan?`Edit — ${editPlan.name}`:'New Plan'} icon="ti-credit-card" width={560} onClose={()=>{setShowNewPlan(false);setEditPlan(null);}}>
          <div style={{display:'grid',gap:12,marginBottom:16}}>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div><label style={labelStyle}>Plan Name *</label><input style={inp} placeholder="e.g. Agency Pro" value={planForm.name} onChange={e=>setPlanForm({...planForm,name:e.target.value})}/></div>
              <div>
                <label style={labelStyle}>Account Type</label>
                <select style={inp} value={planForm.account_type} onChange={e=>setPlanForm({...planForm,account_type:e.target.value})}>
                  <option value="sub">Sub Account</option>
                  <option value="agency">Agency Account</option>
                </select>
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div><label style={labelStyle}>Monthly Price (₹)</label><input style={inp} type="number" min="0" value={planForm.price_monthly_inr} onChange={e=>setPlanForm({...planForm,price_monthly_inr:parseInt(e.target.value)||0})}/></div>
              <div><label style={labelStyle}>Yearly Price (₹)</label><input style={inp} type="number" min="0" value={planForm.price_yearly_inr} onChange={e=>setPlanForm({...planForm,price_yearly_inr:parseInt(e.target.value)||0})}/></div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:12}}>
              <div><label style={labelStyle}>Sub-accounts <span style={{fontSize:10,color:C.faint}}>(-1=∞)</span></label><input style={inp} type="number" min="-1" value={planForm.sub_account_limit} onChange={e=>setPlanForm({...planForm,sub_account_limit:parseInt(e.target.value)||0})}/></div>
              <div><label style={labelStyle}>Contacts <span style={{fontSize:10,color:C.faint}}>(-1=∞)</span></label><input style={inp} type="number" min="-1" value={planForm.features.contacts_limit} onChange={e=>setPlanForm({...planForm,features:{...planForm.features,contacts_limit:parseInt(e.target.value)||0}})}/></div>
              <div><label style={labelStyle}>Users <span style={{fontSize:10,color:C.faint}}>(-1=∞)</span></label><input style={inp} type="number" min="-1" value={planForm.features.users_limit} onChange={e=>setPlanForm({...planForm,features:{...planForm.features,users_limit:parseInt(e.target.value)||0}})}/></div>
            </div>
            <div style={{background:'#f9fafb',borderRadius:10,padding:'14px 16px'}}>
              <div style={{fontSize:12,fontWeight:600,color:C.text,marginBottom:10}}>Features</div>
              <FeatureToggle label="AI Features" checked={planForm.features.ai} onChange={v=>setPlanForm({...planForm,features:{...planForm.features,ai:v}})}/>
              <FeatureToggle label="White-label Branding" checked={planForm.features.white_label} onChange={v=>setPlanForm({...planForm,features:{...planForm.features,white_label:v}})}/>
              <FeatureToggle label="Custom Domain" checked={planForm.features.custom_domain} onChange={v=>setPlanForm({...planForm,features:{...planForm.features,custom_domain:v}})}/>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
              <div><label style={labelStyle}>Trial Days <span style={{fontSize:10,color:C.faint}}>(0 = no trial)</span></label><input style={inp} type="number" min="0" value={planForm.trial_days} onChange={e=>setPlanForm({...planForm,trial_days:parseInt(e.target.value)||0})}/></div>
              <div><label style={labelStyle}>Sort Order</label><input style={inp} type="number" min="0" value={planForm.sort_order} onChange={e=>setPlanForm({...planForm,sort_order:parseInt(e.target.value)||0})}/></div>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:8}}>
              <input type="checkbox" id="is_active" checked={planForm.is_active} onChange={e=>setPlanForm({...planForm,is_active:e.target.checked})} style={{accentColor:C.primary,cursor:'pointer'}}/>
              <label htmlFor="is_active" style={{fontSize:13,color:C.text,cursor:'pointer'}}>Active (visible to clients)</label>
            </div>
          </div>
          <div style={{display:'flex',gap:8}}>
            <Btn onClick={handleSavePlan} disabled={planSaving}><i className="ti ti-device-floppy"/> {planSaving?'Saving...':editPlan?'Update Plan':'Create Plan'}</Btn>
            <Btn variant="outline" onClick={()=>{setShowNewPlan(false);setEditPlan(null);}}>Cancel</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}
